import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  ErrorBoundary,
  Loader,
  navigationItems as allNavigationItems,
  APP_ROUTE_PREFIXES,
} from '@lucid/lego';
import { useStore } from '../../store/store';
import {
  useUserIdentity,
  IMPORTER_LOCALES,
  IMPORTER_LOCALE_VALUES,
  IMPORTER_DEFAULT_LOCALE,
  UBER_LOCALE_VALUES,
  UBER_DEFAULT_LOCALE,
} from '@lucid/portal-core';
import { useGetLabels, updatePartnerUserLanguage } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';
import { isNavigationEnabled, isProfileSettingsEnabled } from '../../config/featureFlags';
import { NAVIGATION_LABELS_ID, DEFAULT_LOCALE } from '../../config/constants';
import { useEntitlements } from '../../hooks/useEntitlements';
import usePageTitle from '../../hooks/usePageTitle';
import styles from './LayoutWrapper.module.scss';

/** Environments where the region selector is visible in the Settings slider */
const REGION_SELECTOR_ENVS = new Set(['development', 'stage']);

/** Locale options shown for importer-role users in the region selector (grouped for @lucid/lego) */
const IMPORTER_LOCALE_OPTIONS = [
  {
    groupLabel: 'Europe',
    data: IMPORTER_LOCALES.map(({ value, label }) => ({ value, label })),
  },
];

/** Environments where the Knowledge Center link is visible in the Settings slider */
const KNOWLEDGE_CENTER_ENVS = new Set(['development', 'qa', 'stage']);

const LayoutWrapper: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const toggleTheme = useStore((state) => state.toggleTheme);
  const locale = useStore((state) => state.locale);
  const setLocale = useStore((state) => state.setLocale);
  const language = useStore((state) => state.language);
  const setLanguage = useStore((state) => state.setLanguage);
  const isSidebarCollapsed = useStore((state) => state.isSidebarCollapsed);
  const setSidebarCollapsed = useStore((state) => state.setSidebarCollapsed);
  const location = useLocation();
  const navigate = useNavigate();
  const lastRedirectPath = useRef<string | null>(null);

  usePageTitle();

  const { userIdentity } = useUserIdentity();
  // isImporter is derived from the /me endpoint's role.marketType field rather than
  // Auth0 JWT roles, which may use role codes that don't match the old IMPORTER_HQ_ROLE_STRINGS.
  const isImporter = userIdentity?.role?.marketType === 'Importer';
  const isUber = userIdentity?.role?.marketType === 'Uber';
  // /me returns preferredLanguage as a full locale (e.g. 'en-US') — reduce to the bare language code
  const preferredLanguageCode =
    userIdentity?.preferredLanguage?.split(/[-_]/)[0]?.toLowerCase() ?? 'en';
  const hasAppliedPreferredLanguageRef = useRef(false);
  const { entitlements, isLoading: isIdentityLoading } = useEntitlements();
  const setEntitlements = useStore((state) => state.setEntitlements);

  useEffect(() => {
    if (!userIdentity || hasAppliedPreferredLanguageRef.current) return;
    setLanguage(preferredLanguageCode);
    hasAppliedPreferredLanguageRef.current = true;
  }, [userIdentity, preferredLanguageCode, setLanguage]);

  // Sync entitlements into the store so route guards (module-level) can read them.
  useEffect(() => {
    if (!isIdentityLoading) {
      setEntitlements(entitlements);
    }
  }, [entitlements, isIdentityLoading, setEntitlements]);

  // Filter navigation items (top-level and children) using /me entitlements.
  // While identity is loading, pass undefined so all items render rather than
  // an empty sidebar — isNavigationEnabled treats undefined as "show all".
  const navigationItems = useMemo(() => {
    const activeEntitlements = isIdentityLoading ? undefined : entitlements;
    return allNavigationItems
      .filter((item) => isNavigationEnabled(item.id, activeEntitlements))
      .map((item) => ({
        ...item,
        childrens: item.childrens?.filter((child) =>
          isNavigationEnabled(child.id, activeEntitlements)
        ),
      }));
  }, [entitlements, isIdentityLoading]);

  // Fetch navigation labels from Contentful
  const { data: navigationLabels } = useGetLabels({
    id: NAVIGATION_LABELS_ID,
    locale: convertToContentfulLocale(locale),
    language,
  });

  // Handle language change
  const handleLanguageChange = useCallback(
    (languageCode: string | null) => {
      setLanguage(languageCode);
      // null means "use the locale's native language" (e.g. 'nl' for nl-NL), not English
      const preferredLanguage = languageCode ?? locale.split('-')[0] ?? 'en';
      updatePartnerUserLanguage(preferredLanguage).catch((error) => {
        console.error('Failed to update preferred language:', error);
      });
    },
    [locale, setLanguage]
  );

  // Sync store locale and language with URL locale on mount and path changes
  useEffect(() => {
    const currentPath = location.pathname;
    // Match locale at start of path, with or without trailing slash
    const localeMatch = /^\/([a-z]{2}-[a-z]{2})(?:\/|$)/i.exec(currentPath);

    if (localeMatch) {
      const urlLocale = localeMatch[1].toLowerCase();
      if (urlLocale !== locale) {
        setLocale(urlLocale);
        // Keep labels aligned with the user's saved language preference.
        if (!isImporter) {
          setLanguage(preferredLanguageCode);
        } else {
          setLanguage(null);
        }
      }
    } else if (locale && locale !== 'en-us') {
      // Store has a non-English locale but URL has no prefix — add it
      const targetPath = `/${locale}${currentPath}`;
      // Guard against infinite redirects by tracking the last redirect target
      if (lastRedirectPath.current !== targetPath) {
        lastRedirectPath.current = targetPath;
        navigate(targetPath, { replace: true });
      }
    }
  }, [
    location.pathname,
    locale,
    isImporter,
    preferredLanguageCode,
    setLocale,
    setLanguage,
    navigate,
  ]);

  // Default importer users to en-be if their current locale isn't in the importer locale list.
  // Must also navigate to update the URL — updating only the store leaves the URL pointing at the
  // old locale (e.g. /de-de/) which causes the URL-sync effect above to immediately revert the
  // store back to de-de, creating an infinite setState loop.
  useEffect(() => {
    if (!isIdentityLoading && isImporter && !IMPORTER_LOCALE_VALUES.has(locale)) {
      const currentPath = location.pathname;
      const localeMatch = /^\/([a-z]{2}-[a-z]{2})(\/.*)?$/i.exec(currentPath);
      const pathWithoutLocale = localeMatch ? localeMatch[2] || '/' : currentPath;
      setLocale(IMPORTER_DEFAULT_LOCALE);
      navigate(`/${IMPORTER_DEFAULT_LOCALE}${pathWithoutLocale}`, { replace: true });
    }
  }, [isIdentityLoading, isImporter, locale, setLocale, navigate, location.pathname]);

  // Reset non-importer users (agency, AR support) away from importer-only locales.
  // Happens when a user switches accounts or the persisted locale from a previous
  // importer session is still in the Zustand store.
  useEffect(() => {
    if (!isIdentityLoading && !isImporter && IMPORTER_LOCALE_VALUES.has(locale)) {
      const currentPath = location.pathname;
      const localeMatch = /^\/([a-z]{2}-[a-z]{2})(\/.*)?$/i.exec(currentPath);
      const pathWithoutLocale = localeMatch ? localeMatch[2] || '/' : currentPath;
      setLocale(DEFAULT_LOCALE);
      navigate(`/${DEFAULT_LOCALE}${pathWithoutLocale}`, { replace: true });
    }
  }, [isIdentityLoading, isImporter, locale, setLocale, navigate, location.pathname]);

  // Uber users are locked to en-us, which never carries a locale prefix in the URL
  // (matches the URL-sync effect's existing en-us-has-no-prefix rule above).
  useEffect(() => {
    if (!isIdentityLoading && isUber && !UBER_LOCALE_VALUES.has(locale)) {
      const currentPath = location.pathname;
      const localeMatch = /^\/([a-z]{2}-[a-z]{2})(\/.*)?$/i.exec(currentPath);
      const pathWithoutLocale = localeMatch ? localeMatch[2] || '/' : currentPath;
      setLocale(UBER_DEFAULT_LOCALE);
      navigate(pathWithoutLocale, { replace: true });
    }
  }, [isIdentityLoading, isUber, locale, setLocale, navigate, location.pathname]);

  const hasAppliedIdentityLocaleRef = useRef(false);

  useEffect(() => {
    if (isIdentityLoading || isImporter || hasAppliedIdentityLocaleRef.current) return;
    const identityLocale = userIdentity?.locale;
    if (!identityLocale) return;

    hasAppliedIdentityLocaleRef.current = true;
    const normalizedLocale = identityLocale.toLowerCase().replace(/_/g, '-');
    if (locale === DEFAULT_LOCALE && normalizedLocale !== locale) {
      const currentPath = location.pathname;
      const localeMatch = /^\/([a-z]{2}-[a-z]{2})(\/.*)?$/i.exec(currentPath);
      const pathWithoutLocale = localeMatch ? localeMatch[2] || '/' : currentPath;
      setLocale(normalizedLocale);
      setLanguage(preferredLanguageCode);
      navigate(`/${normalizedLocale}${pathWithoutLocale}`, { replace: true });
    }
  }, [
    isIdentityLoading,
    isImporter,
    userIdentity?.locale,
    preferredLanguageCode,
    locale,
    setLocale,
    setLanguage,
    navigate,
    location.pathname,
  ]);

  const showRoleDropdown = import.meta.env.VITE_SHOW_ROLE_DROPDOWN === 'true';

  const handleLocaleChange = (newLocale: string) => {
    // Update store first
    setLocale(newLocale);

    // Importers have no language toggle.
    if (!isImporter) {
      setLanguage(preferredLanguageCode);
    } else {
      setLanguage(null);
    }

    // Navigate to the new locale URL using React Router
    const currentPath = location.pathname;
    const currentLocaleMatch = /^\/([a-z]{2}-[a-z]{2})(\/.*)?$/i.exec(currentPath);

    let newPath: string;
    if (currentLocaleMatch) {
      // There's an existing locale in the path
      const pathWithoutLocale = currentLocaleMatch[2] || '/';

      if (newLocale === 'en-us') {
        // For en-us, remove locale prefix
        newPath = pathWithoutLocale;
      } else {
        // For non-en-us, add locale prefix
        newPath = `/${newLocale}${pathWithoutLocale}`;
      }
      navigate(newPath, { replace: true });
    }
  };

  // Show the region selector on local/dev and stage for everyone, and always for
  // importer users (including production) so they can switch between market locales.
  // undefined hides it in Layout.
  const onLocaleChange =
    REGION_SELECTOR_ENVS.has(import.meta.env.VITE_ENV) || isImporter
      ? handleLocaleChange
      : undefined;

  const handleError = (error: Error) => {
    console.error('Route Error:', error);
  };

  const showKnowledgeCenter = KNOWLEDGE_CENTER_ENVS.has(import.meta.env.VITE_ENV);

  // The docs-mfe (Docusaurus) is always served at /knowledge-center/.
  // In development, docs-mfe runs on a different port so VITE_KNOWLEDGE_CENTER_BASE_URL
  // provides the full origin (e.g. http://localhost:3003). In qa/stage it's the same origin
  // (potentially including a locale path like /en-be) — strip any path to get the origin only.
  const knowledgeCenterBase = import.meta.env.VITE_KNOWLEDGE_CENTER_BASE_URL ?? '';
  const knowledgeCenterHref = knowledgeCenterBase
    ? `${new URL(knowledgeCenterBase).origin}/knowledge-center/`
    : '/knowledge-center/';

  if (isIdentityLoading) {
    return (
      <div className={styles.identityLoader}>
        <Loader size={48} theme={theme} />
      </div>
    );
  }

  return (
    <Layout
      theme={theme}
      onToggleTheme={toggleTheme}
      showRoleDropdown={showRoleDropdown}
      currentPath={location.pathname}
      locale={locale}
      onLocaleChange={onLocaleChange}
      localeOptions={isImporter ? IMPORTER_LOCALE_OPTIONS : undefined}
      selectedLanguage={isImporter ? undefined : language}
      onLanguageChange={isImporter ? undefined : handleLanguageChange}
      isSidebarCollapsed={isSidebarCollapsed}
      onSidebarCollapseChange={setSidebarCollapsed}
      sidebarLabels={navigationLabels}
      navigationItems={navigationItems}
      onNavigate={navigate}
      ownRoutePrefixes={APP_ROUTE_PREFIXES.partnerPortal}
      rolesNamespace={`https://${import.meta.env.VITE_AUTH0_DOMAIN}/roles`}
      onProfileClick={
        isProfileSettingsEnabled()
          ? () =>
              window.open(
                import.meta.env.VITE_PROFILE_SETTINGS_URL,
                '_blank',
                'noopener,noreferrer'
              )
          : undefined
      }
      knowledgeCenterLink={
        showKnowledgeCenter
          ? {
              href: knowledgeCenterHref,
              label: navigationLabels?.['sidebar.knowledgeCenter'] ?? 'Knowledge Center',
            }
          : undefined
      }
    >
      <ErrorBoundary theme={theme} onError={handleError} showDetails={import.meta.env.DEV}>
        <Suspense fallback={<div className={styles.suspenseFallback} />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
};

export default LayoutWrapper;
